<template>
  <!-- 版次資料庫（2026-09-20）：NFT 平台停站後的自記錄，一頁一個系列（/nft/<name>/）。資料層形狀見 src/data/<work>-editions.json
       （keys／labels／values／counts／editions[{n,hash,at,r,f}]，選配 chords／palettes），縮圖走 R2 側車清單。
       預設只鋪 40 隻、「再看 40 隻」續；卡片是作品頁同一張 GenomeCard。文案（kicker／h1／lead／facts／provenance／links）由外層元件給。 -->
  <section class="sf-db">
    <header class="sf-db-head">
      <p class="sf-db-kicker">{{ copy.kicker }}</p>
      <h1>{{ copy.h1 }}</h1>
      <p class="sf-db-lead" :lang="zh ? 'zh-Hant' : 'en'">{{ copy.lead }}</p>
      <dl class="sf-db-facts">
        <div v-for="f in copy.facts" :key="f[0]"><dt>{{ f[0] }}</dt><dd>{{ f[1] }}</dd></div>
      </dl>
    </header>

    <section class="sf-db-filters" :aria-label="zh ? '篩選' : 'Filters'">
      <div class="sf-db-toolbar">
        <label class="sf-db-search"><span>{{ zh ? '編號或 hash' : 'Number or hash' }}</span><input v-model.trim="query" type="search" :placeholder="copy.searchHint || (zh ? '#12 或 oo…' : '#12 or oo…')" autocomplete="off" spellcheck="false" /></label>
        <div class="sf-db-sort" role="group" :aria-label="zh ? '排序' : 'Sort'">
          <button v-for="s in sorts" :key="s.key" type="button" :class="{ on: sort === s.key }" @click="sort = s.key">{{ s.label }}</button>
        </div>
        <button v-if="activeCount" type="button" class="sf-db-clear" @click="clearAll">{{ zh ? `清除篩選（${activeCount}）` : `Clear filters (${activeCount})` }}</button>
      </div>
      <div v-for="g in groupList" :key="g.key" class="sf-db-group">
        <span class="sf-db-group-name">{{ g.label }}</span>
        <div class="sf-db-chips">
          <button v-for="v in g.values" :key="v.raw" type="button" class="sf-db-chip" :class="{ on: isOn(g.key, v.raw) }" :aria-pressed="isOn(g.key, v.raw)" @click="toggle(g.key, v.raw)"><i v-if="v.swatches" class="sf-db-chip-pal" aria-hidden="true"><b v-for="c in v.swatches" :key="c" :style="{ background: c }"></b></i>{{ v.label }}<small>{{ v.count }}</small></button>
        </div>
      </div>
    </section>

    <section class="sf-db-results">
      <div class="work-gallery-heading"><span class="work-section-label">{{ zh ? '圖鑑' : 'Field guide' }}</span><span>{{ zh ? `顯示 ${visible.length} ／ 符合 ${filtered.length} ／ 共 ${data.supply}` : `showing ${visible.length} / ${filtered.length} match / ${data.supply} total` }}</span></div>
      <p v-if="!filtered.length" class="sf-db-empty">{{ zh ? '沒有一版同時符合這幾個條件。' : 'No edition matches all of these at once.' }}</p>
      <div v-else class="work-genome-grid" :class="'work-genome-cols-' + columns">
        <genome-card v-for="it in visible" :key="it.hash" :id="'edition-' + it.n" :item="it" :zh="zh" :compact="expandable && !expanded.has(it.n)" :square="thumbWidth === thumbHeight" :expandable="expandable" @toggle="flip(it.n)" />
      </div>
      <div v-if="visible.length < filtered.length" class="sf-db-more">
        <button type="button" @click="shown += PAGE">{{ zh ? `再看 ${Math.min(PAGE, filtered.length - visible.length)} ${unit}` : `Show ${Math.min(PAGE, filtered.length - visible.length)} more` }}</button>
        <span>{{ zh ? `還有 ${filtered.length - visible.length} ${unit}` : `${filtered.length - visible.length} remaining` }}</span>
      </div>
    </section>

    <!-- 附錄（選配）：一個系列自己的鏈上史料，例如波奇的九十四句 PochiAction；copy.appendix = { title, note, head:[…], rows:[[…],…] } -->
    <details v-if="copy.appendix" class="sf-db-appendix">
      <summary>{{ copy.appendix.title }}</summary>
      <p v-if="copy.appendix.note" :lang="zh ? 'zh-Hant' : 'en'">{{ copy.appendix.note }}</p>
      <table>
        <thead><tr><th v-for="h in copy.appendix.head" :key="h">{{ h }}</th></tr></thead>
        <tbody><tr v-for="(r, i) in copy.appendix.rows" :key="i"><td v-for="(c, j) in r" :key="j">{{ c }}</td></tr></tbody>
      </table>
    </details>

    <footer class="sf-db-foot">
      <p :lang="zh ? 'zh-Hant' : 'en'">{{ copy.provenance }}</p>
      <p class="sf-db-links">
        <template v-for="l in copy.links" :key="l.label">
          <router-link v-if="l.to" :to="l.to">{{ l.label }}</router-link>
          <a v-else :href="l.href" target="_blank" rel="noopener">{{ l.label }} ↗</a>
        </template>
      </p>
    </footer>
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import GenomeCard from '@/components/work/GenomeCard.vue'
import { pickAccent } from '@/components/work/genome-utils.mjs'

const props = defineProps({
  data: { type: Object, required: true },        // <work>-editions.json
  thumbs: { type: Object, required: true },      // 側車清單（media-manifest-<set>.json）
  thumbSet: { type: String, required: true },    // 側車清單裡的 set 名
  copy: { type: Object, required: true },        // { kicker, h1, lead, facts:[[k,v]], provenance, links:[{to|href,label}], searchHint?, unit? }
  groups: { type: Array, default: () => [] },    // 篩選籤的 key（順序即顯示順序）
  headline: { type: Array, default: () => [] },  // compact 卡亮的性狀 key
  colorKeys: { type: Array, default: () => [] }, // [主色 key, 暗色 key]；沒有就用 palette 第一色
  paletteKey: String,                            // 有 data.palettes 時：拿哪個性狀的值去查調色盤
  liveFor: { type: Function, default: () => '' },// e → 活的連結
  cardTitle: { type: String, default: '' },
  liveLabel: { type: String, default: '' },      // 卡上活的連結的字（魚：打開這隻活的；花：打開這一版）
  expandable: { type: Boolean, default: true },  // 性狀少的系列（花的靈魂三個）直接全展，不用「展開基因」
  glyph: { type: Boolean, default: true },       // 卡上的魚形（只有靈魂魚有）
  hidden: { type: Array, default: () => [] },   // 不上卡的 key（例：花的靈魂的 theme_id 只是主題的序號）
  columns: { type: Number, default: 4 },
  thumbWidth: { type: Number, default: 540 },   // 縮圖尺寸：方形系列 540²；直式系列（百岳《島嶼》9:16）給 540×960，卡片照原比例不裁
  thumbHeight: { type: Number, default: 540 },
})

const route = useRoute()
const zh = computed(() => route.path.startsWith('/zh/'))
const T = (a, b) => (zh.value ? a : b)
const PAGE = 40
const unit = computed(() => props.copy.unit || T('版', ''))
const data = props.data
const K = data.keys
const idx = Object.fromEntries(K.map((k, i) => [k, i]))
const labelOf = k => data.labels[k]?.[zh.value ? 'zh' : 'en'] || k
const valueOf = (k, v) => { const s = String(v); const m = data.values[s]; return m ? m[zh.value ? 'zh' : 'en'] : s }
const rarityOf = (k, v) => (data.counts[k]?.[String(v)] || 0) / data.supply

// 篩選籤：同組多選是「或」，跨組是「且」
const groupList = computed(() => props.groups.map(k => ({ key: k, label: labelOf(k), values: Object.entries(data.counts[k] || {}).map(([raw, count]) => ({ raw, count, label: valueOf(k, raw), swatches: props.paletteKey === k ? (data.palettes?.[raw] || []).slice(0, 6) : null })) })))
const active = reactive({})
const isOn = (k, raw) => !!active[k]?.has(raw)
const toggle = (k, raw) => { if (!active[k]) active[k] = new Set(); const s = active[k]; s.has(raw) ? s.delete(raw) : s.add(raw); if (!s.size) delete active[k]; shown.value = PAGE }   // 先掛進 reactive 再取回，拿到的才是代理過的 Set
const clearAll = () => { for (const k of Object.keys(active)) delete active[k]; query.value = ''; shown.value = PAGE }
const activeCount = computed(() => Object.values(active).reduce((n, s) => n + s.size, 0) + (query.value ? 1 : 0))

const query = ref('')
const sorts = computed(() => [{ key: 'n', label: T('編號', 'Number') }, { key: 'rare', label: T('最稀有優先', 'Rarest first') }, { key: 'at', label: T('鑄造順序', 'Mint order') }])
const sort = ref('n')
const shown = ref(PAGE)
watch([query, sort], () => { shown.value = PAGE })

const filtered = computed(() => {
  const q = query.value.replace(/^#/, '').toLowerCase()
  let list = data.editions.filter(e => {
    for (const [k, set] of Object.entries(active)) if (!set.has(String(e.f[idx[k]]))) return false
    if (q) { if (/^\d+$/.test(q)) { if (String(e.n) !== q) return false } else if (!e.hash.toLowerCase().startsWith(q)) return false }
    return true
  })
  if (sort.value === 'rare') list = [...list].sort((a, b) => a.r - b.r || a.n - b.n)
  else if (sort.value === 'at') list = [...list].sort((a, b) => a.at.localeCompare(b.at) || a.n - b.n)
  return list
})

const pad = n => String(n).padStart(3, '0')
const thumb = n => props.thumbs.sets?.[props.thumbSet]?.mint?.files?.[pad(n) + '.webp']?.url || ''
// 大圖（選配）：側車清單同一個 set 的 full 種類有這一版，卡片多一個「大圖」連結
const full = n => props.thumbs.sets?.[props.thumbSet]?.full?.files?.[pad(n) + '.webp']?.url || ''
const toItem = e => {
  const palette = props.paletteKey && data.palettes ? data.palettes[String(e.f[idx[props.paletteKey]])] || [] : null
  const [ck, mk] = props.colorKeys
  const color = ck ? e.f[idx[ck]] : pickAccent(palette)
  const minColor = mk ? e.f[idx[mk]] : pickAccent((palette || []).filter(c => c !== color), color)
  const chordName = data.chords ? e.f[idx.atmosphere] : null
  return {
    n: e.n, hash: e.hash, title: props.cardTitle || data.title, image: thumb(e.n), imageWidth: props.thumbWidth, imageHeight: props.thumbHeight,
    imageAspect: props.thumbWidth === props.thumbHeight ? null : `${props.thumbWidth} / ${props.thumbHeight}`, full: full(e.n),
    imageAlt: T(`${props.cardTitle || data.title} #${e.n}，鑄造擷圖`, `${props.cardTitle || data.title} #${e.n}, capture at mint`),
    color, minColor, palette, paletteName: props.paletteKey ? valueOf(props.paletteKey, e.f[idx[props.paletteKey]]) : '', glyph: props.glyph,
    minted: T(`鑄造 ${e.at}`, `minted ${e.at}`), live: props.liveFor(e), liveLabel: props.liveLabel,
    chord: chordName ? { name: chordName, ...(data.chords[chordName] || { notes: [], bass: [] }) } : null,
    features: K.filter(k => !props.hidden.includes(k) && e.f[idx[k]] !== '').map(k => ({ key: k, name: labelOf(k), value: valueOf(k, e.f[idx[k]]), raw: e.f[idx[k]], rarity: rarityOf(k, e.f[idx[k]]) })),
    headline: props.headline,
  }
}
const visible = computed(() => filtered.value.slice(0, shown.value).map(toItem))

const expanded = reactive(new Set())
const flip = n => { expanded.has(n) ? expanded.delete(n) : expanded.add(n) }

// 深連結 ?n=12：把那一版篩出來、展開、捲到它
onMounted(() => {
  const n = Number(route.query.n), first = data.first ?? 1   // 波奇的 token 從 #0 起（data.first = 0）
  // 編號不連號的系列（百岳《島嶼》569 枚散在 #1–#2842）用「資料裡有沒有這一版」判斷
  const exists = data.editions.length && data.editions[data.editions.length - 1].n - first + 1 !== data.supply ? data.editions.some(e => e.n === n) : (n >= first && n <= first + data.supply - 1)
  if (route.query.n !== undefined && exists) { query.value = '#' + n; expanded.add(n); requestAnimationFrame(() => document.getElementById('edition-' + n)?.scrollIntoView({ block: 'start' })) }
})
</script>

<style>
.sf-db{--sf-paper:#f2eee5;--sf-muted:#aaa396;--sf-line:#2a2823;background:var(--bg0,#080808);color:var(--sf-paper);text-align:left;font-family:var(--font-body);padding:36px max(24px,5vw) 96px;max-width:1840px;margin:auto;overflow-wrap:anywhere}
.sf-db-head{max-width:96ch;margin:0 0 28px}
.sf-db-kicker{font-family:ui-monospace,"SF Mono",Menlo,monospace;font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--sf-muted);margin:0 0 10px}
.sf-db h1{font-family:var(--font-display),serif;font-weight:400;font-size:clamp(30px,4.2vw,52px);line-height:1.1;margin:0 0 18px;color:var(--sf-paper)}
.sf-db-lead{font-size:15.5px;line-height:1.9;color:#d9d6cc;margin:0 0 22px;max-width:80ch}
.sf-db-facts{display:flex;flex-wrap:wrap;gap:10px 28px;margin:0;padding:14px 0 0;border-top:1px solid #38362f}
.sf-db-facts div{display:grid;gap:3px}.sf-db-facts dt{font-family:ui-monospace,"SF Mono",Menlo,monospace;font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:#7b776d;margin:0}.sf-db-facts dd{margin:0;font-size:13px;color:var(--sf-paper)}
.sf-db-filters{border-top:1px solid #38362f;padding:18px 0 6px;display:grid;gap:12px}
.sf-db-toolbar{display:flex;flex-wrap:wrap;gap:10px 18px;align-items:end;padding-bottom:6px}
.sf-db-search{display:grid;gap:4px;font-family:ui-monospace,"SF Mono",Menlo,monospace;font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:#7b776d}
.sf-db-search input{appearance:none;background:var(--bg2);border:0;color:var(--ink);font:13px/1.4 ui-monospace,"SF Mono",Menlo,monospace;padding:9px 16px;min-width:220px;border-radius:var(--r-pill);letter-spacing:.02em}.sf-db-search input:focus{outline:none;box-shadow:0 0 0 1px var(--gold)}
.sf-db-sort{display:flex;gap:4px}
.sf-db-sort button,.sf-db-clear{appearance:none;background:var(--tint-2);border:0;border-radius:var(--r-pill);color:var(--ink-2);font:11px/1 ui-monospace,"SF Mono",Menlo,monospace;letter-spacing:.06em;padding:10px 14px;cursor:pointer;transition:var(--t-hover)}.sf-db-sort button:hover,.sf-db-clear:hover{background:var(--tint-3);color:var(--ink)}
.sf-db-sort button.on{background:var(--ink);color:#000}
.sf-db-clear{color:var(--gold)}
.sf-db-group{display:grid;grid-template-columns:96px minmax(0,1fr);gap:8px 14px;align-items:baseline;padding:6px 0;border-top:1px solid #1c1b18}
.sf-db-group-name{font-family:ui-monospace,"SF Mono",Menlo,monospace;font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:#7b776d}
.sf-db-chips{display:flex;flex-wrap:wrap;gap:6px}
.sf-db-chip{appearance:none;background:var(--tint-2);border:0;color:var(--ink-2);font:12px/1 var(--font-body);padding:7px 12px;border-radius:var(--r-pill);cursor:pointer;display:inline-flex;gap:7px;align-items:center;transition:var(--t-hover)}
.sf-db-chip small{font-family:ui-monospace,"SF Mono",Menlo,monospace;font-size:9px;color:#7b776d}
.sf-db-chip-pal{display:inline-flex;gap:1px;height:10px}.sf-db-chip-pal b{display:block;width:6px;height:10px;border:0}
.sf-db-chip:hover{background:var(--tint-3);color:var(--ink)}.sf-db-chip.on{color:var(--ink);background:var(--gold-tint)}.sf-db-chip.on small{color:var(--gold)}
.sf-db-results{margin-top:6px}
.sf-db-empty{color:var(--sf-muted);font-size:14px;padding:24px 0}
.sf-db-more{display:flex;gap:16px;align-items:center;justify-content:center;padding:34px 0 8px;font-family:ui-monospace,"SF Mono",Menlo,monospace;font-size:11px;color:#7b776d}
.sf-db-more button{appearance:none;background:var(--tint-2);border:0;color:var(--ink);font:12px/1 ui-monospace,"SF Mono",Menlo,monospace;letter-spacing:.08em;padding:13px 26px;border-radius:var(--r-pill);cursor:pointer;transition:var(--t-hover)}.sf-db-more button:hover{background:var(--tint-3);color:var(--gold)}
.sf-db-appendix{max-width:96ch;margin:48px 0 8px;border-top:1px solid var(--sf-line);padding-top:18px}.sf-db-appendix summary{cursor:pointer;font-size:13px;letter-spacing:.06em;color:var(--sf-paper)}.sf-db-appendix p{color:var(--sf-muted);font-size:13px;line-height:1.7;margin:12px 0}.sf-db-appendix table{border-collapse:collapse;width:100%;font-size:13px;margin-top:8px}.sf-db-appendix th,.sf-db-appendix td{text-align:left;padding:6px 10px 6px 0;border-bottom:1px solid var(--sf-line);vertical-align:top}.sf-db-appendix th{font-weight:400;color:var(--sf-muted);font-size:11px;letter-spacing:.12em;text-transform:uppercase}.sf-db-appendix td:first-child{font-family:ui-monospace,"SF Mono",Menlo,monospace;white-space:nowrap;color:var(--sf-muted)}
.sf-db-foot{border-top:1px solid #38362f;margin-top:56px;padding-top:20px;max-width:96ch}
.sf-db-foot p{font-size:13px;line-height:1.8;color:var(--sf-muted);margin:0 0 12px}
.sf-db-links{display:flex;flex-wrap:wrap;gap:8px 22px}.sf-db-links a{color:var(--sf-paper);text-decoration:none;border-bottom:1px solid #4a4638;padding-bottom:1px}.sf-db-links a:hover{color:var(--gold,#d9c267);border-color:var(--gold,#d9c267)}
@media(max-width:820px){.sf-db{padding-top:24px}.sf-db-group{grid-template-columns:1fr;gap:6px}.sf-db-search input{min-width:0;width:100%}.sf-db-search{width:100%}}
</style>
